/* import __COLOCATED_TEMPLATE__ from './seller-profile-project-list-item.hbs'; */
import { notEmpty, readOnly } from '@ember/object/computed';
import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@ember/component';
import { htmlSafe } from '@ember/template';
import { showModal } from "sh-www/utils/modal-controls";
import { isUrl } from "../../utils/misc";
import { EMBER_ROUTE_NAMES } from 'sh-www/constants/routes';
import { LSG_REVIEW_STARS__STAR_LABELS } from "../lsg/lsg-reviews-stars";
import { task } from 'ember-concurrency';
import { closeModal } from 'sh-www/utils/modal-controls';
import { handleErrorResponseWithModal } from 'sh-www/utils/handles-errors';
import LightboxOverlay from 'sh-www/components/modals/overlays/lightbox';
import ConfirmationModal from 'sh-www/components/modals/confirmation';
import clsx from 'clsx';

const CLASS_NAME = 'SH-c-seller-profile-project-list-item';
const SELLER_EDIT_BUTTON_CLASS_NAME = `${CLASS_NAME}__seller-edit-button`;
const SELLER_EDIT_BUTTON_NEED_SAMPLE_CLASS_NAME = `${SELLER_EDIT_BUTTON_CLASS_NAME}--needs-work-sample`;

export default Component.extend({
  /**
   * HTML Component properties
   */
  tagName: 'section',
  classNames: [CLASS_NAME],
  classNameBindings: [
    `projectDigest.isPendingPublication:${CLASS_NAME}--pending-publication`
  ],

  CLASS_NAME,

  /**
   * Register services
   */
  applicationCommandService: service('commands/application-commands'),
  videoThumbnailService: service('video-thumbnail-from-url'),
  applicationStateManager: service('state-managers/application'),
  errorTracker: service('error-tracker'),
  media: service(),
  projectdigestCommandService: service('commands/projectdigest-commands'),
  session: service(),

  currentUser: readOnly('session.currentUser'),

  /**
   * @type {Projectdigest|null}   Main model for the list item
   */
  projectDigest: null,

  /** @param {Boolean} isViewingSelf */
  isViewingSelf: false,

  /** @param {Function} saveAdminNoteTask */
  saveAdminNoteTask: () => {},

  /** @param {Function} showFeaturedDigestInModal */
  showFeaturedDigestInModal: () => {},

  /** @param {Function} onUnverifyProjectDigestTask */
  onUnverifyProjectDigestTask: () => {},

  /** @param {Function} onVerifyProjectDigestTask */
  onVerifyProjectDigestTask: () => {},

  /** @param {Function} onDeleteProjectDigestTask */
  onDeleteProjectDigestTask: () => {},

  /** @param {Function} onRestoreProjectDigestTask */
  onRestoreProjectDigestTask: () => {},

  onSetBadLink: null,

  /** @param {Boolean} isEditing */
  isEditing: false,

  /** @param {String} reviewEditComments */
  reviewEditComments: '',

  /** @param {Number} reviewStars */
  reviewEditStars: 1,

  /** @type {function(Object): Promise<any>} */
  updateReview: null,

  /**
   * @type {Array<String>}    List of strings we will send the to stars component to show when buyer selects a review rating.
   */
  _ratingTextLabels: LSG_REVIEW_STARS__STAR_LABELS,

  /** @param {Function} setPinned */
  setPinned: (projectdigestId, isPinned) => {
    throw new Error('define setPinned');
  },

  isPinned: false,

  sellerEditButtonStyles: computed('projectDigest.link', function () {
    return clsx(
      SELLER_EDIT_BUTTON_CLASS_NAME,
      !this.projectDigest.link && SELLER_EDIT_BUTTON_NEED_SAMPLE_CLASS_NAME
    );
  }),

  brandOrCompanyName: computed('projectDigest.company.name', 'projectDigest.application.brand.displayedName', function() {
    const { application, company } = this.projectDigest;
    return htmlSafe(application?.brand?.displayedName ?? company.name);
  }),

  /**
   * @type {String}     True if we do NOT have an actual image to show.
   */
  _hasNoImageBorder: computed('_projectDigestImage', function() {
    return !this._projectDigestImage;
  }),

  /**
   * @type {Boolean}    True when we want the description in the right column and not taking the full width of the cell.
   */
  _isDescriptionInRightColumn: computed('media.isLargeAndUp', 'projectDigest.isPendingPublication', function() {
    return !this.get('projectDigest.isPendingPublication') && this.get('media.isLargeAndUp');
  }),

  /** @type {Boolean} Whether there is a company image to display. */
  _hasCompanyImage: computed('projectDigest.company.image.{cloudId,isPlaceholderImage}', function () {
    const { image } = this.projectDigest.company;
    return image?.cloudId && !image.isPlaceholderImage;
  }),

  needsWorkSample: computed('projectDigest.link', function () {
    return Boolean(!this.projectDigest.link);
  }),

  /** @type {String|null} The project digest image, if it exists. */
  _projectDigestImage: computed(
    'projectDigest.thumbnailUrl',
    'projectDigest.link',
    function () {
      const imgSrc = this.projectDigest.thumbnailUrl;
      const digestLink = this.get('projectDigest.link');

      if (imgSrc) {
        return imgSrc;
      }

      if (!digestLink || !isUrl(digestLink)) {
        return '';
      }

      return this.get('videoThumbnailService').getThumb(digestLink);
    },
  ),

  /** @type {Boolean} Whether the project digest image failed to load. */
  _hasImageError: false,

  /** @type {Boolean} Whether the image is missing. */
  _isMissingImage: computed('_projectDigestImage', '_hasImageError', function () {
    return !this.get('_projectDigestImage') || this.get('_hasImageError');
  }),

  /**
   * @type {Boolean}        True if we can click this project digest.
   */
  _isDigestClickable: computed('projectDigest.{isLinkableVideo,isEmbedVideo,badLink}', function() {
    return !this.projectDigest.badLink && (this.projectDigest.isLinkableVideo || this.projectDigest.isEmbedVideo);
  }),

  /** @type {Boolean} Whether this project is verified. */
  _isVerified: readOnly('projectDigest.confirm'),

  /** @type {Boolean} Whether this project is deleted. */
  _isDeleted: notEmpty('projectDigest.deletedAt'),

  _showDeletedItem: computed('_isDeleted', 'currentUser.isAdmin', function () {
    return this._isDeleted && this.currentUser.isAdmin;
  }),

  _showVideoBrokenError: computed(
    'isViewingSelf',
    'projectDigest.oembed.{isBrokenVideo,url}',
    function () {
      const { oembed } = this.projectDigest;
      return Boolean(
        oembed
        && oembed.isBrokenVideo
        && oembed.url.length > 0
        && this.isViewingSelf
      );
    }
  ),

  _showVideoThumbnail: computed(
    '_projectDigestImage',
    'projectDigest.isPendingPublication',
    function () {
      return !this.projectDigest.isPendingPublication
        && Boolean(this._projectDigestImage);
    }
  ),

  /** @type {Boolean} Whether the user is allowed to edit the verification info. */
  _canEditVerificationInfo: computed('projectDigest.wasDoneOnPlatform', '_isVerified', function () {
    return !this.projectDigest.wasDoneOnPlatform && !this._isVerified;
  }),

  /** @type {Array<Object>} The options shown to an admin. */
  _adminEditMenuItems: computed(
    'projectDigest.{id,wasDoneOnPlatform,badLink}',
    '_isVerified',
    '_isDeleted',
    '_showVideoThumbnail',
    function () {
      const projectDigest = this.projectDigest;
      const menu = [];

      // Delete / Un-delete
      if (this._isDeleted) {
        menu.push({
          title: 'Show on profile',
          onClick: () => {
            showModal(this, 'confirmation', {
              titleText: 'Show project on profile?',
              bodyText: "This will show the project on this profile.<br>It will be displayed to clients and freelancers, and it will be included when calculating a freelancer's scores.",
              actionButtonText: 'Show project',
              confirmAction: () => {
                this.onRestoreProjectDigestTask.perform(projectDigest);
              },
              cancelButtonText: 'Cancel',
            });
          },
        });
      } else {
        // Verify / Unverify
        if (this._isVerified) {
          menu.push({
            title: 'Remove verification',
            onClick: () => {
              showModal(this, 'confirmation', {
                titleText: 'Remove project verification?',
                bodyText: "This will remove the project's verified status.<br>The project will no longer have a ‘Verified’ badge and will be treated as an unverified project.",
                actionButtonText: 'Remove verification',
                confirmAction: () => {
                  this.onUnverifyProjectDigestTask.perform(projectDigest);
                },
                cancelButtonText: 'Cancel',
              });
            },
          });
        } else {
          menu.push({
            title: 'Mark as verified',
            onClick: () => {
              showModal(this, 'admin/verify-project-digest-modal', {
                projectDigest,
                verifyTask: this.onVerifyProjectDigestTask,
                saveAdminNote: this.saveAdminNoteTask,
              });
            },
          });
        }

        menu.push({
          title: 'Hide from profile',
          onClick: () => {
            showModal(this, 'confirmation', {
              titleText: 'Hide project from profile?',
              bodyText: "This will hide the project from this profile.<br>It will no longer be displayed to clients or freelancers and will not be included when calculating a freelancer's scores.",
              actionButtonText: 'Hide project',
              confirmAction: () => this.onDeleteProjectDigestTask.perform(projectDigest, true),
              cancelButtonText: 'Cancel',
            });
          },
        });

        // only allow for projects done on platform
        if (this.projectDigest.wasDoneOnPlatform) {
          menu.push({
            title: 'Go to project page',
            onClick: () => {
              this.applicationStateManager.setNextRoute(
                EMBER_ROUTE_NAMES.PROJECT__DETAILS,
                null,
                this.projectDigest.application.id,
              );
            },
          });
        }

        if (!projectDigest.badLink && this._showVideoThumbnail) {
          menu.push({
            title: 'Bad link',
            onClick: () => {
              this.handleBadLinkClicked();
            },
          });
        }
      }

      return menu;
    }
  ),

  /**
   * @type {Boolean}                  True when we will show the right arrow.
   */
  _isViewingOwnDigest: computed('projectDigest.user.id', 'currentUser.id', function() {
    const digestUserId = this.projectDigest?.user.id;
    const currentUserId = this.currentUser?.id;
    return Boolean(digestUserId && currentUserId) && digestUserId === currentUserId;
  }),

  /** @type {Array<Object>} The options in the edit menu. */
  _editMenuItems: computed(
    'currentUser.isAdmin',
    '_adminEditMenuItems.[]',
    function () {
      return this.currentUser.isAdmin ? this._adminEditMenuItems : [];
    },
  ),

  /** @inheritDoc */
  init() {
    this._super(...arguments);
    this.set('isPinned', this.projectDigest.pinned);
  },

  handleBadLinkClicked: action(function() {
    const { onSetBadLink, projectDigest } = this;
    showModal(this, ConfirmationModal,  {
      titleText: 'Does this project have a broken link?',
      bodyText: 'The creator will be notified via email.',
      actionButtonText: 'Confirm',
      confirmAction: () => {
        onSetBadLink.perform(projectDigest);
      },
      cancelButtonText: 'Cancel',
    });
  }),

  actions: {
    onSellerEditClicked(needsWorkSample) {
      showModal(this, LightboxOverlay, {
        allClassifications: this.classificationList,
        allServices: this.serviceList,
        digestsToShowForVideoCarousel: [this.projectDigest],
        allowSellerEdits: true,
        screen: 'edit',
        needsWorkSample,

        closeSecondaryScreen: () => {
          closeModal(this);
        },
      });
    },

    /**
     * When the user clicks on the project digest.
     *
     * @param {MouseEvent} e
     */
    onDigestClicked: function(e) {
      e.stopPropagation();
      e.preventDefault();

      const projectDigest = this.projectDigest;

      this.showFeaturedDigestInModal(projectDigest);
    },

    onImageError() {
      this.set('_hasImageError', true);
    },

    onImageLoad() {
      this.set('_hasImageError', false);
    },

    onEditReviewClicked() {
      this.setProperties({
        reviewEditComments: this.projectDigest.review.comment,
        reviewEditStars: this.projectDigest.review.score,
        isEditing: true,
      });
    },

    onCancelEditClicked() {
      this.toggleProperty('isEditing');
    },

    onStarClick(starCountSelected) {
      this.set('reviewEditStars', starCountSelected + 1);
    },

    handleCommentInput(value) {
      this.set('reviewEditComments', value)
    },

    togglePin(e){
      e.stopPropagation();
      e.preventDefault();
      this.togglePinTask.perform();
    },
  },

  togglePinTask: task(function*(){
    const pinState = this.isPinned;

    try {
      yield this.setPinned(this.projectDigest.id, this.set('isPinned', !pinState));
    } catch (e) {
      this.set('isPinned', pinState);

      e.errors.pinned ?
      this.applicationStateManager.addToast(e.errors.pinned) :
      this.applicationStateManager.addToast('There was a problem while attempting to pin/unpin a project. Please try again.');
    }
  }).drop(),

  onSubmitClicked: task(function*(){
    try {
      yield this.updateReview(
        this.projectDigest.application,
        this.reviewEditStars,
        this.reviewEditComments,
        this.projectDigest.id,
      );
    } finally {
      this.set('isEditing', false);
    }
  }),

});
